"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Switch } from "../../../components/ui/switch"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Link, Plus, Edit, Trash2, GripVertical, Eye } from "lucide-react"

const links = [
  {
    id: 1,
    label: "Sunday Service Live Stream",
    url: "https://youtube.com/live/service",
    visible: true,
    order: 1,
  },
  {
    id: 2,
    label: "Prayer Request Form",
    url: "/prayer-request",
    visible: true,
    order: 2,
  },
  {
    id: 3,
    label: "Youth Camp Registration",
    url: "/youth-camp",
    visible: true,
    order: 3,
  },
  {
    id: 4,
    label: "Donate Online",
    url: "/donate",
    visible: false,
    order: 4,
  },
  {
    id: 5,
    label: "Contact Us",
    url: "/contact",
    visible: true,
    order: 5,
  },
]

export function LinkTreeSection({allUsers,adminUsers,comments}:any) {
  const [editingLink, setEditingLink] = useState<(typeof links)[0] | null>(null)
  const [isAddingLink, setIsAddingLink] = useState(false)
  const [formData, setFormData] = useState({
    label: "",
    url: "",
  })

  const handleEdit = (link: (typeof links)[0]) => {
    setEditingLink(link)
    setFormData({
      label: link.label,
      url: link.url,
    })
  }

  const handleAdd = () => {
    setIsAddingLink(true)
    setFormData({
      label: "",
      url: "",
    })
  }

  const handleSave = () => {
    console.log("Saving link:", formData)
    setEditingLink(null)
    setIsAddingLink(false)
  }

  const handleDelete = (linkId: number) => {
    console.log("Deleting link:", linkId)
  }

  const toggleVisibility = (linkId: number) => {
    console.log("Toggling visibility for link:", linkId)
  }

  const visibleLinks = links.filter((link) => link.visible)

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  LinkTree Page Builder
                </CardTitle>
                <CardDescription>Manage your public-facing LinkTree-style page</CardDescription>
              </div>
              <Button onClick={handleAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Link
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {links.map((link) => (
                <Card key={link.id} className={`${!link.visible ? "opacity-60" : ""}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="cursor-move">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{link.label}</h4>
                        <p className="text-sm text-muted-foreground truncate">{link.url}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={link.visible} onCheckedChange={() => toggleVisibility(link.id)}/>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(link)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(link.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Live Preview
            </CardTitle>
            <CardDescription>How your LinkTree page will appear to visitors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-lg">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-2">Ministry Links</h2>
                <p className="text-muted-foreground">Connect with us</p>
              </div>
              <div className="space-y-3 max-w-sm mx-auto">
                {visibleLinks.map((link) => (
                  <div
                    key={link.id}
                    className="bg-white p-3 rounded-lg shadow-sm border text-center cursor-pointer hover:shadow-md transition-shadow"
                  >
                    <span className="font-medium">{link.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <p className="text-xs text-muted-foreground">
                  {visibleLinks.length} active link{visibleLinks.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={editingLink !== null || isAddingLink}
        onOpenChange={() => {
          setEditingLink(null)
          setIsAddingLink(false)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLink ? "Edit Link" : "Add New Link"}</DialogTitle>
            <DialogDescription>Configure the link label and destination</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="label">Link Label</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                placeholder="Enter link label"
              />
            </div>
            <div>
              <Label htmlFor="url">Destination URL</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com or /internal-page"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingLink(null)
                  setIsAddingLink(false)
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave}>{editingLink ? "Update Link" : "Add Link"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
